/* ============================================================
   ARTTEESHOW. V3 · ERASED teaser page (/pages/erased)
   Behaviour for sections/ats-erased-teaser.liquid. No libraries.

   Every state is decided here, in the browser, never with Liquid's "now" (Shopify caches
   pages, so server time can be stale):
     teaser   before the drop time: the countdown ticks
     pending  at or after the drop, /products/{handle}.js answers 404 or fails:
              "Dropping now", checked again every 10 seconds
     live     the product answers with available: true: "Out now", Shop button, and
              forwarding to the product when the setting is on
     soldout  the product answers with available: false: "Sold out · Archived"

   Testing only: ?ats_state=teaser|pending|live|soldout forces a state (and never
   forwards, so the screen can be inspected); ?ats_drop=<ISO 8601> overrides the drop time.
   ============================================================ */

(() => {
  'use strict';

  if (window.__atsErasedTeaser) return;
  window.__atsErasedTeaser = true;

  const PHASES = ['teaser', 'pending', 'live', 'soldout'];
  const RETRY_MS = 10000;
  const FORWARD_DELAY_MS = 1500;
  const ANNOUNCE = {
    pending: 'Dropping now.',
    live: 'Out now.',
    soldout: 'Sold out. Archived.',
  };

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const scrollBehaviour = () => (reducedMotion() ? 'instant' : 'smooth');
  const pad = (n) => String(Math.max(0, n)).padStart(2, '0');

  /* ISO 8601 with an offset is read as written; without one it is read as UK time. */
  const parseDrop = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return NaN;
    if (/(z|[+-]\d{2}:?\d{2})$/i.test(value)) return Date.parse(value);
    const wall = value.slice(11, 16);
    for (const offset of ['+01:00', '+00:00']) {
      const t = Date.parse(value + offset);
      if (Number.isNaN(t)) return NaN;
      try {
        const uk = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        }).format(t);
        if (!wall || uk === wall) return t;
      } catch (e) {
        return t;
      }
    }
    return Date.parse(value + '+00:00');
  };

  const split = (ms) => {
    const total = Math.max(0, Math.floor(ms / 1000));
    return {
      d: Math.floor(total / 86400),
      h: Math.floor((total % 86400) / 3600),
      m: Math.floor((total % 3600) / 60),
      s: total % 60,
    };
  };

  class Teaser {
    constructor(root) {
      this.root = root;
      this.cleanups = [];
      this.phase = null;
      this.timer = 0;
      this.retry = 0;
      this.forwardTimer = 0;
      this.signedUp = Boolean(root.querySelector('[data-ats-notify-ok]'));

      const rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.handle = (root.dataset.atsHandle || '').trim();
      this.productUrl = `${rootUrl}products/${encodeURIComponent(this.handle)}`;
      this.forward = root.dataset.atsForward === 'true';

      const params = new URLSearchParams(window.location.search);
      const forced = params.get('ats_state');
      this.forced = PHASES.includes(forced) ? forced : null;
      this.target = parseDrop(params.get('ats_drop') || root.dataset.atsDrop);

      root.classList.add('ats-js');
      this.announcer = root.querySelector('[data-ats-announce]');
      this.cells = [...root.querySelectorAll('[data-ats-cd]')];
      this.barTime = root.querySelector('[data-ats-bar-time]');

      this.initHeaderOffset();
      this.initReveal();
      this.initForms();
      this.initBar();
      this.start();
    }

    on(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.cleanups.push(() => target.removeEventListener(type, handler, options));
    }

    destroy() {
      window.clearInterval(this.timer);
      window.clearTimeout(this.retry);
      window.clearTimeout(this.forwardTimer);
      this.cleanups.forEach((fn) => {
        try {
          fn();
        } catch (e) {
          /* already gone */
        }
      });
      this.cleanups = [];
    }

    /* ---------- State ----------------------------------------------------------- */

    setPhase(phase) {
      if (phase === this.phase) return;
      const first = this.phase === null;
      this.phase = phase;
      this.root.dataset.atsPhase = phase;
      const scopes = [this.root, this.bar].filter(Boolean);
      scopes.forEach((scope) => {
        scope.querySelectorAll('[data-ats-when]').forEach((el) => {
          el.hidden = !el.dataset.atsWhen.split(/\s+/).includes(phase);
        });
      });
      /* Without a valid drop time there is nothing to count down to. */
      if (phase === 'teaser' && Number.isNaN(this.target)) {
        this.root.querySelectorAll('[data-ats-countdown]').forEach((el) => {
          el.hidden = true;
        });
      }
      if (!first && this.announcer && ANNOUNCE[phase]) {
        this.announcer.textContent = ANNOUNCE[phase];
      }
      if (this.updateBar) this.updateBar();
    }

    start() {
      if (this.forced) {
        this.setPhase(this.forced);
        if (this.forced === 'teaser') this.tick(true);
        return;
      }
      if (Number.isNaN(this.target) || Date.now() < this.target) {
        this.setPhase('teaser');
        if (!Number.isNaN(this.target)) this.tick(false);
        return;
      }
      /* Arrived after the drop: say so while the product is checked, then act on it. */
      this.setPhase('pending');
      this.check(true);
    }

    /* Counts down every second. At zero, switches state without a reload. */
    tick(forced) {
      const render = () => {
        const left = this.target - Date.now();
        const t = split(left);
        this.cells.forEach((cell) => {
          const value = pad(t[cell.dataset.atsCd]);
          if (cell.textContent !== value) cell.textContent = value;
        });
        if (this.barTime) this.barTime.textContent = `${pad(t.d)}:${pad(t.h)}:${pad(t.m)}:${pad(t.s)}`;
        if (left <= 0 && !forced) {
          window.clearInterval(this.timer);
          this.setPhase('pending');
          this.check(false);
        }
      };
      render();
      window.clearInterval(this.timer);
      this.timer = window.setInterval(render, 1000);
      this.on(document, 'visibilitychange', render);
    }

    async check(arrivedAfterDrop) {
      window.clearTimeout(this.retry);
      let product = null;
      try {
        const response = await fetch(`${this.productUrl}.js?ats=${Date.now()}`, {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (response.ok) product = await response.json();
      } catch (e) {
        product = null;
      }

      if (!product) {
        this.setPhase('pending');
        this.retry = window.setTimeout(() => this.check(false), RETRY_MS);
        return;
      }
      if (product.available) {
        this.setPhase('live');
        if (this.forward) {
          const go = () => window.location.replace(this.productUrl);
          if (arrivedAfterDrop) go();
          else this.forwardTimer = window.setTimeout(go, FORWARD_DELAY_MS);
        }
        return;
      }
      this.setPhase('soldout');
    }

    /* ---------- Header offset --------------------------------------------------
       The live header is fixed and publishes no height. Measure it while unscrolled. */
    initHeaderOffset() {
      const header = document.getElementById('HeaderWrapper');
      if (!header) return;
      const measure = () => {
        if (header.classList.contains('header-scrolled')) return;
        const h = Math.round(header.getBoundingClientRect().height);
        if (h > 20 && h < 200) this.root.style.setProperty('--header-h', `${h}px`);
      };
      measure();
      let frame = 0;
      this.on(window, 'resize', () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(measure);
      });
    }

    /* ---------- Reveal motion, as on the product page ----------------------------- */
    initReveal() {
      const nodes = [...this.root.querySelectorAll('[data-ats-reveal]')];
      if (!nodes.length) return;
      if (reducedMotion() || !('IntersectionObserver' in window)) {
        nodes.forEach((n) => n.classList.add('is-in'));
        return;
      }
      const groups = new Map();
      nodes.forEach((n) => {
        const group = n.closest('[data-ats-reveal-group]');
        if (!group) return;
        const list = groups.get(group) || [];
        n.style.setProperty('--reveal-delay', `${Math.min(list.length, 8) * 0.06}s`);
        list.push(n);
        groups.set(group, list);
      });
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
      );
      const begin = () => {
        const vh = window.innerHeight || 800;
        nodes.forEach((n) => {
          const box = n.getBoundingClientRect();
          if (box.top < vh && box.bottom > 0) n.classList.add('is-in');
          else io.observe(n);
        });
      };
      if (document.visibilityState === 'hidden') {
        const onVisible = () => {
          if (document.visibilityState !== 'visible') return;
          document.removeEventListener('visibilitychange', onVisible);
          begin();
        };
        this.on(document, 'visibilitychange', onVisible);
      } else {
        begin();
      }
      this.cleanups.push(() => io.disconnect());
    }

    /* ---------- Forms ------------------------------------------------------------
       Both forms post to Shopify on their own. Shopify reports a result on every
       customer form of the page, so the error stays only on the form that was sent
       (named in the #anchor Shopify returns to); the success line stays on both,
       because it is true of the visitor either way. */
    initForms() {
      const sent = window.location.hash.slice(1);
      const forms = [...this.root.querySelectorAll('form.ats-notify')];
      if (sent && forms.some((f) => f.id === sent)) {
        forms.forEach((form) => {
          if (form.id === sent) return;
          form.querySelectorAll('[data-ats-notify-error]').forEach((el) => el.remove());
          form.querySelectorAll('[aria-invalid]').forEach((el) => {
            el.removeAttribute('aria-invalid');
            el.removeAttribute('aria-describedby');
          });
        });
      }
      const ok = (sent && document.getElementById(sent)?.querySelector('[data-ats-notify-ok]')) ||
        this.root.querySelector('[data-ats-notify-ok]');
      if (ok) {
        const focusOk = () => window.setTimeout(() => ok.focus({ preventScroll: true }), 60);
        if (document.readyState === 'complete') focusOk();
        else this.on(window, 'load', focusOk, { once: true });
      }
    }

    /* ---------- Mobile sticky bar ------------------------------------------------
       Moved to <body> so position: fixed means the screen (the theme keeps a transform
       on #MainContent). Rises once the hero's form area has scrolled past, steps aside
       while the final-call form is on screen and at the end of the page, and stays
       down for good after a successful sign-up. Measured on scroll, so a fast fling
       cannot skip it. */
    initBar() {
      const bar = this.root.querySelector('[data-ats-bar]');
      if (!bar) return;
      this.bar = bar;
      document.body.appendChild(bar);
      this.cleanups.push(() => bar.remove());

      const anchor = this.root.querySelector('[data-ats-hero-actions]');
      const finalForm = this.root.querySelector('.ats-final__form');
      let up = false;
      let frame = 0;

      const check = () => {
        frame = 0;
        let next = false;
        if (!this.signedUp && anchor) {
          const past = anchor.getBoundingClientRect().bottom < 0;
          const vh = window.innerHeight;
          let finalInView = false;
          if (finalForm && !finalForm.hidden) {
            const box = finalForm.getBoundingClientRect();
            finalInView = box.top < vh && box.bottom > 0;
          }
          const atEnd = this.root.getBoundingClientRect().bottom <= vh;
          next = past && !finalInView && !atEnd;
        }
        if (next === up) return;
        up = next;
        bar.classList.toggle('is-up', up);
        bar.setAttribute('aria-hidden', up ? 'false' : 'true');
        bar.inert = !up;
      };
      const schedule = () => {
        if (!frame) frame = requestAnimationFrame(check);
      };
      this.updateBar = schedule;
      this.on(window, 'scroll', schedule, { passive: true });
      this.on(window, 'resize', schedule);
      this.cleanups.push(() => cancelAnimationFrame(frame));

      const notify = bar.querySelector('[data-ats-bar-notify]');
      if (notify) {
        this.on(notify, 'click', () => {
          if (!finalForm) return;
          finalForm.scrollIntoView({ behavior: scrollBehaviour(), block: 'center' });
          const input = finalForm.querySelector('input[type="email"]');
          if (input) input.focus({ preventScroll: true });
        });
      }
      check();
    }
  }

  const instances = new Map();
  const init = (root) => {
    const id = root && root.dataset.sectionId;
    if (!id || instances.has(id)) return;
    instances.set(id, new Teaser(root));
  };
  const destroy = (id) => {
    const instance = instances.get(id);
    if (!instance) return;
    instance.destroy();
    instances.delete(id);
  };
  const start = () => document.querySelectorAll('[data-ats-teaser]').forEach(init);

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-ats-teaser]');
    if (root) {
      destroy(root.dataset.sectionId);
      init(root);
    }
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-ats-teaser]');
    if (root) destroy(root.dataset.sectionId);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
